import { withKeys } from "@/common/helpers/list-keys";
import { cn } from "@/common/helpers/utils";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { DatabaseIcon, SearchXIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export enum EmptyStateActionVariant {
  Default = ButtonVariant.Filled,
  Outline = ButtonVariant.Outlined,
  Secondary = ButtonVariant.Filled,
  Ghost = ButtonVariant.Ghost,
}

type EmptyStateAction = {
  label: string;
  onClick: () => void;
  variant?: EmptyStateActionVariant;
  colorVariant?: ButtonColorVariant;
};

interface EmptyStateProps {
  hasFilters?: boolean;
  hasDefaultFilters?: boolean;
  hasNonDefaultFilters?: boolean;
  hasSearch?: boolean;
  searchTerm?: string;
  noDataTitle?: string;
  noDataDescription?: string;
  noDataIcon?: React.ComponentType<{ className?: string }>;
  noResultsTitle?: string;
  noResultsDescription?: string;
  className?: string;
  onClearFilters?: () => void;
  onResetFilters?: () => void;
  onClearSearch?: () => void;
  actions?: EmptyStateAction[];
}

export function EmptyState({
  hasFilters = false,
  hasDefaultFilters = false,
  hasNonDefaultFilters = false,
  hasSearch = false,
  searchTerm = "",
  noDataTitle,
  noDataDescription,
  noDataIcon,
  noResultsTitle,
  noResultsDescription,
  className,
  onClearFilters,
  onResetFilters,
  onClearSearch,
  actions = [],
}: EmptyStateProps) {
  const { t } = useTranslation("common");

  const resetFiltersLabel = t(hasDefaultFilters ? "RestoreDefaultFilters" : "ClearFilters");

  const getDefaultContent = () => {
    if (hasSearch && searchTerm) {
      return {
        icon: SearchXIcon,
        title: noResultsTitle ?? t("NoResultsForSearchTerm", { searchTerm }),
        description: noResultsDescription ?? t("TryAdjustingSearchOrFilters"),
      };
    }

    if (hasFilters || hasNonDefaultFilters) {
      return {
        icon: SearchXIcon,
        title: noResultsTitle ?? t("NoResultsFound"),
        description: noResultsDescription ?? t("NoItemsMatchCurrentFilters"),
      };
    }

    return {
      icon: noDataIcon ?? DatabaseIcon,
      title: noDataTitle ?? t("NoDataAvailable"),
      description: noDataDescription ?? t("NoItemsToDisplay"),
    };
  };

  const defaultContent = getDefaultContent();
  const { icon: DisplayIcon, title: displayTitle, description: displayDescription } = defaultContent;

  const defaultActions: EmptyStateAction[] = [];

  if (hasSearch && searchTerm && onClearSearch) {
    defaultActions.push({
      label: t("ResetSearch"),
      onClick: onClearSearch,
      variant: EmptyStateActionVariant.Default,
    });
  }

  if (hasFilters && onClearFilters) {
    defaultActions.push({
      label: t("ClearAllFilters"),
      onClick: onClearFilters,
      variant: EmptyStateActionVariant.Default,
    });
  }

  if (hasNonDefaultFilters && onResetFilters) {
    defaultActions.push({
      label: resetFiltersLabel,
      onClick: onResetFilters,
      variant: EmptyStateActionVariant.Default,
    });
  }

  const allActions = [...defaultActions, ...actions];
  const hasActions = allActions.length > 0;

  return (
    <div className={cn("flex min-h-75 flex-col items-center justify-center px-4 py-12 text-center", className)}>
      <div className="bg-muted mb-4 rounded-full p-3">
        <DisplayIcon className="text-muted-foreground h-8 w-8" />
      </div>

      <h3 className="mb-2 text-lg font-semibold">{displayTitle}</h3>

      <p className="text-muted-foreground mb-6 max-w-md">{displayDescription}</p>

      {hasActions && (
        <div className="flex flex-wrap justify-center gap-2">
          {withKeys(allActions, (action, index) => `${action.label}-${index}`).map(({ key, item: action }) => (
            <Button
              key={key}
              type="button"
              variant={(action.variant as string as ButtonVariant) ?? ButtonVariant.Filled}
              colorVariant={action.colorVariant}
              onClick={action.onClick}
              size={ButtonSize.Default}
              className="h-auto min-h-9 max-w-full px-4 text-center whitespace-normal"
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
